import { useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useOnboardingStore } from '../../stores/onboardingStore'
import Step1Details from '../../steps/Step1Details'
import Step2Billing from '../../steps/Step2Billing'
import Step3ManagedBilling from '../../steps/Step3ManagedBilling'
import Step4Owners from '../../steps/Step4Owners'
import Step5Locations from '../../steps/Step5Locations'
import Step6NetworkPlus from '../../steps/Step6NetworkPlus'
import Step7Providers from '../../steps/Step7Providers'
import Step8Kits from '../../steps/Step8Kits'
import Step9Teams from '../../steps/Step9Teams'
import StepReview from '../../steps/StepReview'
import ErrorBoundary from '../ErrorBoundary'

// Maps each 1-based step POSITION (currentStep) to the component that renders
// there. Component file names are historical (Step2Billing etc.) and no longer
// match position — the order below is the live flow order and MUST stay in sync
// with the STEPS array in onboardingStore.js:
//   1 details · 2 locations · 3 owners · 4 networkplus · 5 providers ·
//   6 kits · 7 teams · 8 billing · 9 managedBilling · 10 review
const STEP_MAP = {
  1: Step1Details,
  2: Step5Locations,
  3: Step4Owners,
  4: Step6NetworkPlus,
  5: Step7Providers,
  6: Step8Kits,
  7: Step9Teams,
  8: Step2Billing,
  9: Step3ManagedBilling,
  10: StepReview,
}

const reduced =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

const transition = reduced
  ? { duration: 0 }
  : { duration: 0.32, ease: [0.16, 1, 0.3, 1] }

const exitTransition = reduced
  ? { duration: 0 }
  : { duration: 0.24, ease: 'easeIn' }

export default function StepContainer() {
  const current = useOnboardingStore((s) => s.currentStep)
  const Step = STEP_MAP[current]

  useEffect(() => {
    if (typeof window === 'undefined') return
    // Reset scroll ONCE at the start of the swap. The old late setTimeout(400)/
    // setTimeout(650) calls fired mid-entrance and forced a reflow that read as a
    // flash/flashback — removed. One sync reset + a single rAF is enough.
    const resetTop = () => {
      const content = document.querySelector('.form-content')
      if (content) content.scrollTop = 0
      const shell = document.querySelector('.form-shell')
      if (shell) shell.scrollTop = 0
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }
    resetTop()
    const r1 = requestAnimationFrame(resetTop)
    return () => cancelAnimationFrame(r1)
  }, [current])

  if (!Step) return null

  // mode="popLayout": the exiting page is popped out of layout flow (framer
  // positions it absolutely) so the incoming page never gets pushed/co-exists in
  // normal flow during the swap — clean crossfade, no double-render flashback.
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={current}
        initial={reduced ? { opacity: 1 } : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reduced ? { opacity: 1 } : { opacity: 0, y: -6, transition: exitTransition }}
        transition={transition}
        className="step-container"
      >
        <ErrorBoundary>
          <Step />
        </ErrorBoundary>
      </motion.div>
    </AnimatePresence>
  )
}
